import isNumber from 'lodash/isNumber';
import type {
  AutoBrandingSeenServerResponse,
  BrandKitUpdateServerResponse,
  ColorPaletteInfo,
  ColorPaletteInfoToStore,
  ColorPalettePill,
  UserColorPaletteInfo,
} from '@Components/auto-branding/auto-branding.types';
import {ColorPaletteType} from '@Components/auto-branding/auto-branding.types';
import {StyleType} from '@Libraries/ga-events';
import {canUserAccessBrandKits} from '@Libraries/brands-library';
import type {Component, Editor} from 'grapesjs';
import {PALETTE_APPLIED_EVENT, PALETTE_CUSTOM_STYLE} from '@/email-maker/grapesjs-email-editor-plugin/src/custom-style-types/auto-branding';

const HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY = 'hasSeenAutoBranding';

export const isSameColorPalette = (a: ColorPaletteInfo | null, b: ColorPaletteInfo | null): boolean => {
  if (a === null || b === null) {
    return false;
  }

  if (a.colors.length !== b.colors.length) {
    return false;
  }

  for (let i = 0; i < a.colors.length; i++) {
    if (a.colors[i] !== b.colors[i]) {
      return false;
    }
  }

  return true;
};

export const removeDuplicateColorPalettes = (colorPalettes: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  const uniqueColorPalettes: ColorPaletteInfo[] = [];

  colorPalettes.forEach((colorPalette): void => {
    if (
      !uniqueColorPalettes.some((uniqueColorPalette): boolean => {
        return isSameColorPalette(colorPalette, uniqueColorPalette);
      })
    ) {
      uniqueColorPalettes.push(colorPalette);
    }
  });

  return uniqueColorPalettes;
};

export const changeColorPaletteType = (colorPaletteInfo: ColorPaletteInfo, type: ColorPaletteType): ColorPaletteInfo => {
  return {
    ...colorPaletteInfo,
    type,
  };
};

const createPaletteInfoToStore = (colorPalette: ColorPaletteInfo): string => {
  const infoToStore: ColorPaletteInfoToStore = {
    colors: colorPalette.colors,
  };

  if (colorPalette.type === ColorPaletteType.User && colorPalette.id) {
    infoToStore.id = isNumber(colorPalette.id) ? colorPalette.id : parseInt(colorPalette.id, 10);
    infoToStore.name = colorPalette.name;
  } else {
    infoToStore.customizedFromId = colorPalette.id;
    infoToStore.customizedFromType = colorPalette.type;
    infoToStore.name = colorPalette.name;
  }

  return JSON.stringify(infoToStore);
};

export const storeAppliedPaletteInHtml = (editor: Editor, colorPalette: ColorPaletteInfo): void => {
  const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  const infoToStore = createPaletteInfoToStore(colorPalette);

  mainWrapper.addStyle({[PALETTE_CUSTOM_STYLE]: infoToStore});
  window.dispatchEvent(new CustomEvent(PALETTE_APPLIED_EVENT));
};

export const hasUserSeenAutoBranding = async (): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn()) {
    return true;
  }

  const cachedHasSeen = getHasSeenAutoBrandingCacheValue();
  if (cachedHasSeen) {
    return cachedHasSeen === 1;
  }

  const data = (await window.PMW.readLocal(`autobrandingemailmaker/hasUserSeenAutoBranding`)) as AutoBrandingSeenServerResponse;
  data.hasSeen = !isNumber(data.hasSeen) ? parseInt(data.hasSeen, 10) : data.hasSeen;
  setHasSeenAutoBrandingCacheValue(data.hasSeen);
  return data.hasSeen === 1;
};

const setHasSeenAutoBrandingCacheValue = (hasSeen: number): void => {
  window.PMW.setCacheItem(HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY, hasSeen);
};

const getHasSeenAutoBrandingCacheValue = (): number | null => {
  const cachedHasSeen = window.PMW.getCacheItem(HAS_USER_SEEN_AUTO_BRANDING_CACHE_KEY) as number | undefined;

  return cachedHasSeen ?? null;
};

export const markUserSeenAutoBranding = async (): Promise<void> => {
  if (!window.PMW.isUserLoggedIn()) {
    return;
  }

  await window.PMW.writeLocal(`autobrandingemailmaker/markUserHasSeenAutoBranding`);
};

export const getColorPalettes = async (): Promise<ColorPaletteInfo[]> => {
  const colorPalettes = window.PMW.emails.COLOR_PALETTES ?? (await waitForColorPalette());
  return colorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

const waitForColorPalette = async (): Promise<ColorPaletteInfo[]> => {
  const waitForColorPalettePills = (): Promise<ColorPaletteInfo[]> => {
    const timeOutMs = 50;

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (typeof window.PMW.emails.COLOR_PALETTES !== 'undefined') {
          clearInterval(interval);
          resolve(window.PMW.emails.COLOR_PALETTES);
        }
      }, timeOutMs);
    });
  };

  return window.PMW.emails.COLOR_PALETTES ?? (await waitForColorPalettePills());
};

export const getUserColorPalettes = async (limit = 0, offset = 0): Promise<ColorPaletteInfo[]> => {
  if (!window.PMW.isUserLoggedIn()) {
    return [];
  }

  const userColorPalettes = (await window.PMW.readLocal('autobrandingemailmaker/getUserColorPalettes', {limit, offset})) as UserColorPaletteInfo[];
  return userColorPalettes.map((userColorPalette): ColorPaletteInfo => {
    return {
      id: userColorPalette.id,
      colors: userColorPalette.colors,
      name: userColorPalette.name,
      type: ColorPaletteType.User,
    };
  });
};

export const getBrandKitColorPalettes = async (): Promise<ColorPaletteInfo[]> => {
  if (!window.PMW.isUserLoggedIn() || !canUserAccessBrandKits()) {
    return [];
  }

  const userColorPalettes = (await window.PMW.readLocal('autobrandingemailmaker/getBrandKitColorPalettes')) as UserColorPaletteInfo[];
  return userColorPalettes.map((userColorPalette): ColorPaletteInfo => {
    return {
      id: userColorPalette.customizedFromId,
      colors: userColorPalette.colors,
      name: userColorPalette.name,
      type: ColorPaletteType.BrandKit,
    };
  });
};

export const updateBrandKitFromColorPalette = async (colorPalette: ColorPaletteInfo): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn() || !canUserAccessBrandKits()) {
    return false;
  }

  if (!colorPalette.id || colorPalette.colors.length === 0) {
    return false;
  }

  const data = (await window.PMW.writeLocal('autobrandingemailmaker/updateBrandKitFromColorPalette', {
    brandId: colorPalette.id,
    colors: colorPalette.colors,
  })) as BrandKitUpdateServerResponse;
  return data.updateSuccess;
};

export const getColorPalettePillsToShow = async (): Promise<ColorPalettePill[]> => {
  return window.PMW.emails.COLOR_PALETTE_PILLS ?? (await waitForColorPalettePills());
};

const waitForColorPalettePills = (): Promise<ColorPalettePill[]> => {
  const timeOutMs = 50;

  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (typeof window.PMW.emails.COLOR_PALETTE_PILLS !== 'undefined') {
        clearInterval(interval);
        resolve(window.PMW.emails.COLOR_PALETTE_PILLS);
      }
    }, timeOutMs);
  });
};

export const filterColorPalettesBySearchTerm = (searchTerm: string, colorPalettesToSearchFrom: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  if (colorPalettesToSearchFrom.length === 0) {
    return [];
  }

  const matchesPaletteTag = (palette: ColorPaletteInfo, tag: string): boolean => {
    return (
      palette.tags?.some((paletteTag) => {
        return paletteTag.name.toLowerCase().includes(tag.toLowerCase());
      }) ?? false
    );
  };
  const matchesPaletteName = (palette: ColorPaletteInfo, name: string): boolean => {
    return palette.name?.toLowerCase().includes(name.toLowerCase()) ?? false;
  };

  const filteredColorPalettes = colorPalettesToSearchFrom.filter((palette): boolean => {
    return matchesPaletteName(palette, searchTerm) || matchesPaletteTag(palette, searchTerm);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getColorPalettesByTagIds = (tagIds: number[]): ColorPaletteInfo[] => {
  const colorPalettes = window.PMW.emails.COLOR_PALETTES;

  if (!colorPalettes) {
    return [];
  }

  const paletteContainsAllOfTags = (palette: ColorPaletteInfo, ids: number[]): boolean => {
    return (
      palette.tags?.every((paletteTag) => {
        return ids.includes(paletteTag.id);
      }) ?? false
    );
  };

  const filteredColorPalettes = colorPalettes.filter((palette): boolean => {
    return paletteContainsAllOfTags(palette, tagIds);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getGA4StyleTypeFromColorPalette = (colorPalette: ColorPaletteInfo): string => {
  switch (colorPalette.type) {
    case ColorPaletteType.Admin:
      return StyleType.ALL_STYLES;
    case ColorPaletteType.BrandKit:
      return StyleType.BRAND_KIT;
    case ColorPaletteType.User:
      return StyleType.RECENTS;
    default:
      console.error('Unknown color palette type', colorPalette);
      return 'Unknown';
  }
};
